import { captureMessage } from '@sentry/nextjs';
import Cookies from 'universal-cookie';

import { COOKIE_USER_ID } from '@/constants/cookie.constants';
import { getBrowserId } from '@/general/components/Analytics/Analytics.helpers';
import { throttle } from '@/utils/throttle';

import { CarmaApiTrackingEvent, CarmaTrackingEvent } from './CarmaTrackingApi.types';

const cookies = new Cookies();
const beaconThrottle = 5000;

/**
 * The list events queued to send to our CDP
 */
const eventsQueue: CarmaApiTrackingEvent[] = [];

export const getCarmaTrackingEventsQueue = () => [...eventsQueue];

export const initCarmaEventTracker = () => {
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', handlePageUnload);
  }
};

export const removeCarmaEventTracker = () => {
  // Process the queue before unloading our tracker
  processQueue();
  if (typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', handlePageUnload);
  }
};

export const pushCarmaEvent = (data: CarmaTrackingEvent) => {
  const event = enrichCarmaEvent(data);
  if (event) {
    eventsQueue.push(event);
  }
  throttledProcessQueue();
};

/**
 *  Method in charge of sending all the pending events to our CDP
 */
const processQueue = (): void => {
  if (typeof navigator?.sendBeacon !== 'function' || eventsQueue.length === 0) {
    return;
  }

  const formData = new FormData();
  formData.append('events', JSON.stringify(eventsQueue));
  try {
    navigator.sendBeacon(`${process.env.NEXT_PUBLIC_BIZ_API_URL}/carma-data`, formData);
  } catch (error) {
    captureMessage(`Error sending beacon with: "${error}"`, 'warning');
  }
  // Empty the queue
  eventsQueue.length = 0;
};

const handlePageUnload = () => {
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
    processQueue();
  }
};

const throttledProcessQueue = throttle(processQueue, beaconThrottle);

const enrichCarmaEvent = (data: CarmaTrackingEvent): CarmaApiTrackingEvent | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }
  const origin_url = window.location.toString();
  const timestamp = new Date().toISOString();
  const browser_id = getBrowserId();
  const user_id = cookies.get(COOKIE_USER_ID) ?? undefined;
  return {
    ...data,
    origin_url,
    timestamp,
    browser_id,
    user_id,
  };
};
